import { Context, Data, Effect, Layer, Schema } from 'effect';
import type * as KeyValueStore from 'effect/persistence/KeyValueStore';
import { sessionStorageKeys, SessionStorageService } from './session-storage';

/** The exact length of a `defaultEngine` identifier. */
const ENGINE_ID_LENGTH = 10;

/** A BOM engine identifier: a non-empty string of exactly ten characters. */
const EngineId = Schema.NonEmptyString.check(Schema.isBetweenLength(ENGINE_ID_LENGTH, ENGINE_ID_LENGTH));

/**
 * The parts of the stored account the Configurator uses.
 *
 * The host app writes more than this — `organization` and `allowedIPs` — and decoding drops them. Only what is declared here survives, so the Configurator cannot come to depend on a field it never asked for.
 */
export class Account extends Schema.Class<Account>('Account')(
	Schema.Struct({
		defaultEngine: EngineId,
		id: Schema.NonEmptyString,
		name: Schema.NonEmptyString,
	}),
) {}

/**
 * The account as the host app writes it, with `defaultEngine` left opaque.
 *
 * Decoding runs in two stages so an unusable `defaultEngine` can be reported as its own error rather than as a generic `SchemaError` that says nothing about which field was at fault. Holding the field as an optional unknown here means absent, `null` and wrong-length values all survive stage one and are judged together
 * in stage two.
 */
const StoredAccount = Schema.Struct({
	defaultEngine: Schema.optional(Schema.Unknown),
	id: Schema.NonEmptyString,
	name: Schema.NonEmptyString,
});

/** Raised when the store works but holds no account under `User.Account`. */
export class MissingAccountError extends Data.TaggedError('MissingAccountError')<{
	readonly key: string;
}> {}

/**
 * Raised when the stored account carries no usable `defaultEngine`.
 *
 * Covers absent, `null` and wrong-length values alike, since the Configurator cannot act on any of them. Carries the offending value so a host app contract break can be told apart from a user who simply has no default engine set.
 */
export class MissingDefaultEngineError extends Data.TaggedError('MissingDefaultEngineError')<{
	readonly key: string;
	readonly value: unknown;
}> {}

/**
 * Everything reading the account can fail with.
 *
 * Four distinct causes, and they mean different things: `KeyValueStoreError` is the storage API failing, `MissingAccountError` is nobody having signed in, `MissingDefaultEngineError` is an account without a usable engine, and `SchemaError` is a stored account whose `id` or `name` does not match what the Configurator
 * expects — a host app contract break rather than a user problem.
 */
export type AccountError = KeyValueStore.KeyValueStoreError | MissingAccountError | MissingDefaultEngineError | Schema.SchemaError;

/**
 * A stored account counts as present only when it is a non-empty string.
 *
 * Session storage reports an absent key as `undefined` rather than as a failure, since in a key-value store that is a normal outcome. Here it is not, so this refinement turns the absence into the typed failure above.
 */
function isPresent(value: string | undefined): value is string {
	return value !== undefined && value !== '';
}

const decodeStoredAccount = Schema.decodeEffect(Schema.fromJsonString(StoredAccount));
const decodeEngineId = Schema.decodeUnknownEffect(EngineId);

/** Stage two: judge `defaultEngine` on its own, then assemble the account. */
function withEngineId(stored: typeof StoredAccount.Type): Effect.Effect<Account, MissingDefaultEngineError> {
	return decodeEngineId(stored.defaultEngine).pipe(
		Effect.mapError(
			() =>
				new MissingDefaultEngineError({
					key: sessionStorageKeys.account,
					value: stored.defaultEngine,
				}),
		),
		Effect.map((defaultEngine) => new Account({ defaultEngine, id: stored.id, name: stored.name })),
	);
}

/** Supplies the signed-in account. */
export interface CurrentAccount {
	/**
	 * Reads and decodes the current account.
	 *
	 * An Effect rather than a plain value, because the host app rewrites the key when the user switches account: it has to be read per use, not once when the layer is built.
	 */
	readonly account: Effect.Effect<Account, AccountError>;
}

/** Provides the account the Configurator is acting on behalf of. */
export class AccountService extends Context.Service<AccountService, CurrentAccount>()('@acies/platform/AccountService', {
	/** Builds an account reader over whichever session storage is in context. */
	make: Effect.gen(function* () {
		const { account } = yield* SessionStorageService;
		return {
			account: account.pipe(
				Effect.filterOrFail(isPresent, () => new MissingAccountError({ key: sessionStorageKeys.account })),
				Effect.flatMap(decodeStoredAccount),
				Effect.flatMap(withEngineId),
			),
		};
	}),
}) {
	/** The account reader over any session storage, leaving the storage to the caller. */
	static readonly layerNoDeps: Layer.Layer<AccountService, never, SessionStorageService> = Layer.effect(this)(this.make);

	/** Reads the account the host app placed in session storage. */
	static readonly layer: Layer.Layer<AccountService> = AccountService.layerNoDeps.pipe(Layer.provide(SessionStorageService.layer));

	/** Pins the account explicitly, bypassing the store. */
	static readonly layerAccount = (account: Account): Layer.Layer<AccountService> => Layer.succeed(AccountService, AccountService.of({ account: Effect.succeed(account) }));
}
